import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../../store/useStore";
import { RETURN_STATUS_CONFIG } from "../../../store/slices/returnsSlice";
import { buildAssetUrl } from "../../../utils/api";
import {
  FaUndo,
  FaClock,
  FaCheckCircle,
  FaSyncAlt,
  FaTimesCircle,
  FaBoxOpen,
  FaChevronRight,
  FaTruck,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaTimes,
  FaCalendarAlt,
  FaTag,
  FaInfoCircle,
  FaSpinner,
} from "react-icons/fa";

/* --- Constants --- */

const FILTER_TABS = [
  { id: "ALL",              label: "All Returns" },
  { id: "UNDER_REVIEW",     label: "Under Review" },
  { id: "PICKUP_SCHEDULED", label: "Pickup Scheduled" },
  { id: "APPROVED",         label: "Approved" },
  { id: "PROCESSING",       label: "Processing" },
  { id: "REFUNDED",         label: "Refunded" },
  { id: "REJECTED",         label: "Rejected" },
];

const STATUS_ICON = {
  UNDER_REVIEW:     FaClock,
  NEW:              FaClock,
  PENDING:          FaClock,
  PICKUP_SCHEDULED: FaTruck,
  ASSIGNED:         FaTruck,
  APPROVED:         FaCheckCircle,
  PROCESSING:       FaSyncAlt,
  REFUNDED:         FaCheckCircle,
  COMPLETED:        FaCheckCircle,
  REJECTED:         FaTimesCircle,
  CANCELLED:        FaTimesCircle,
};

/* --- Helpers --- */

const formatDate = (iso) => {
  if (!iso) return "\u2014";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
};

const fmtRs = (val) => `\u20B9${parseFloat(val ?? 0).toFixed(2)}`;

const getStatusConfig = (statusKey) =>
  RETURN_STATUS_CONFIG[statusKey] ?? {
    label: statusKey, bg: "bg-amber-50", text: "text-amber-700",
    border: "border-amber-200", dot: "bg-amber-400",
  };

/* --- Return Detail Drawer --- */

const ReturnDetailDrawer = ({ item, onClose }) => {
  const navigate = useNavigate();
  const { fetchReturnByRef } = useStore();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailError, setDetailError] = useState("");

  // Fetch full detail on open
  useEffect(() => {
    if (!item) return;
    const ref = item.return_ref ?? item.return_request_number;
    if (!ref) {
      setDetail(null);
      setDetailError("The return reference is missing, so details could not be loaded.");
      setLoading(false);
      return;
    }
    let isCurrentRequest = true;
    setDetail(null);
    setDetailError("");
    setLoading(true);
    fetchReturnByRef(ref)
      .then((d) => {
        if (isCurrentRequest && d) setDetail(d);
        else if (isCurrentRequest) setDetailError("The return details were not included in the API response.");
      })
      .catch((error) => {
        if (isCurrentRequest) setDetailError(error.message || "Failed to load return details.");
      })
      .finally(() => { if (isCurrentRequest) setLoading(false); });
    return () => { isCurrentRequest = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.return_ref, item?.return_request_number]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  if (!detail) {
    return (
      <div className="fixed inset-0 z-[80] flex justify-end">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Return Request</p>
              <p className="font-mono text-sm font-bold text-gray-800">
                {item.return_ref ?? item.return_request_number ?? item.return_id}
              </p>
            </div>
            <button onClick={onClose} className="rounded-xl bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-200">
              Close
            </button>
          </div>
          <div className="flex flex-1 items-center justify-center p-6 text-center">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <FaSpinner className="h-4 w-4 animate-spin" />
                Loading return details from the API...
              </div>
            ) : (
              <p className="max-w-sm text-sm text-red-600">{detailError || "Return details are unavailable."}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const statusKey  = (detail.status ?? "").toUpperCase();
  const cfg        = getStatusConfig(statusKey);
  const StatusIcon = STATUS_ICON[statusKey] ?? FaClock;
  const displayRef = detail.return_ref ?? detail.return_request_number ?? `#${detail.return_id}`;

  const returnItems = detail.return_items?.length
    ? detail.return_items
    : Array.isArray(detail.items) ? detail.items : [];

  const timeline   = detail.timeline;
  const statusInfo = detail.current_status_info;
  const pickup     = detail.pickup_details;
  const refund     = detail.refund_information;

  const orderId    = detail.order_id ?? detail.order?.order_id;
  const cleanNavId = orderId ? String(orderId).replace(/^#/, "").trim() : null;
  const refundFields = refund ? [
    ["Refund Status", refund.refund_status],
    ["Refund Amount", refund.refund_amount != null ? fmtRs(refund.refund_amount) : null],
    ["Refund Method", refund.refund_method],
    ["Preference", refund.refund_preference],
    ["UPI ID", refund.upi_id],
    ["Bank Account Number", refund.bank_account_number],
    ["IFSC Code", refund.bank_ifsc_code],
    ["Account Holder", refund.bank_account_holder],
    ["Bank Name", refund.bank_name],
    ["Estimated Refund Time", refund.estimated_refund_time],
    ["Refund Reference", refund.refund_reference],
    ["Refund Date", refund.refund_date],
    ["Refund Time", refund.refund_time],
  ] : [];

  return (
    <div className="fixed inset-0 z-[80] flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer panel */}
      <div
        className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl overflow-hidden"
        style={{ animation: "slideInRight 0.28s cubic-bezier(0.22,1,0.36,1)" }}
      >
        {/* Top colour strip */}
        <div className={`h-1.5 w-full shrink-0 ${cfg.bg}`} />

        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 bg-gray-50/80 px-5 py-4 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Return Request</span>
              <span className="font-mono text-base font-extrabold text-gray-900">{displayRef}</span>
            </div>
            {detail.return_request_id && (
              <p className="mt-0.5 text-[11px] text-gray-400">Request ID: {detail.return_request_id}</p>
            )}
            {(detail.order_number || cleanNavId) && (
              <p className="mt-0.5 text-xs font-semibold text-gray-500">
                Order {cleanNavId || detail.order_number}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${cfg.bg} ${cfg.text} ${cfg.border}`}>
              <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
              <StatusIcon className="h-3 w-3" />
              {detail.status_label ?? detail.status}
            </span>
            {detail.status_label && detail.status && detail.status_label !== detail.status && (
              <span className="text-[9px] font-mono text-gray-400">{detail.status}</span>
            )}
            <button
              onClick={onClose}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition"
            >
              <FaTimes className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">

          {loading && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-400">
              <FaSpinner className="h-4 w-4 animate-spin" />
              <span>Loading full details...</span>
            </div>
          )}

          {/* Status Banner */}
          {statusInfo && (
            <div className="rounded-2xl border border-amber-200/80 bg-amber-50/70 p-4">
              <div className="flex items-center gap-2 mb-1">
                <FaInfoCircle className="h-4 w-4 text-amber-600 shrink-0" />
                <p className="text-sm font-extrabold text-amber-900">{statusInfo.title}</p>
              </div>
              <p className="text-xs font-medium text-amber-800">{statusInfo.message}</p>
              {statusInfo.sub_message && (
                <p className="mt-1 text-[11px] text-amber-700">{statusInfo.sub_message}</p>
              )}
            </div>
          )}

          {/* Timeline */}
          {timeline && typeof timeline === "object" && (
            <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Return Progress
              </p>
              <div className="relative">
                {/* vertical connector line */}
                <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-gray-200 rounded" />
                <div className="space-y-4">
                  {Object.entries(timeline).map(([key, step]) => {
                    const isCompleted = step.status === "COMPLETED";
                    const isCurrent   = step.status === "CURRENT";
                    return (
                      <div key={key} className="flex items-start gap-3 relative">
                        <span
                          className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold border-2 ${
                            isCompleted
                              ? "bg-emerald-600 border-emerald-600 text-white"
                              : isCurrent
                              ? "bg-amber-500 border-amber-500 text-white animate-pulse"
                              : "bg-white border-gray-200 text-gray-400"
                          }`}
                        >
                          {isCompleted ? "\u2713" : isCurrent ? "\u25CF" : "\u25CB"}
                        </span>
                        <div className="flex-1 min-w-0 pt-1">
                          <p className={`text-sm font-bold ${
                            isCurrent ? "text-amber-800" : isCompleted ? "text-emerald-800" : "text-gray-400"
                          }`}>
                            {step.title}
                          </p>
                          {step.status && (
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                              {step.status}
                            </p>
                          )}
                          <p className="text-[11px] text-gray-500 mt-0.5">{step.description}</p>
                          {(step.date || step.time) && (
                            <p className="mt-0.5 text-[10px] font-semibold text-gray-400">
                              {step.date ?? ""}{step.time ? ` \u00B7 ${step.time}` : ""}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Return Type & Meta */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-3.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Return Type</p>
              <p className="text-sm font-bold text-gray-800">
                {detail.return_type_label ?? detail.return_type}
              </p>
              {detail.return_type && detail.return_type_label && (
                <p className="mt-0.5 text-[10px] font-mono text-gray-400">{detail.return_type}</p>
              )}
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-3.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Requested On</p>
              <p className="text-[11px] font-semibold text-gray-800">{formatDate(detail.created_at)}</p>
            </div>
          </div>

          {/* Reason chips */}
          {detail.reasons?.length > 0 && (
            <div className="rounded-2xl border border-amber-100 bg-amber-50/40 p-3.5">
              <div className="flex items-center gap-1.5 mb-2">
                <FaTag className="h-3 w-3 text-amber-600" />
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Return Reasons</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {detail.reasons.map((r) => (
                  <span
                    key={r.code}
                    className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-amber-800"
                  >
                    {r.label ?? r.code}
                    {r.code && r.label && r.code !== r.label && (
                      <span className="ml-1 font-mono text-[9px] text-gray-400">({r.code})</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Items */}
          {returnItems.length > 0 && (
            <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-3.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-3">
                Items in this Return ({returnItems.length})
              </p>
              <div className="space-y-3">
                {returnItems.map((ri, idx) => {
                  const name   = ri.product_name ?? ri.name ?? ri.product?.name ?? "";
                  const qty    = ri.quantity;
                  const unitName = ri.unit_name ?? ri.unit ?? ri.variant_name ?? "";
                  const unit   = unitName ? ` \u00B7 ${unitName}` : "";
                  const imgSrc = ri.image ?? ri.product_image;
                  const imgUrl = imgSrc ? buildAssetUrl(imgSrc) : null;
                  return (
                    <div key={ri.return_item_id ?? ri.order_item_id ?? idx} className="flex items-center gap-3">
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={name}
                          className="h-12 w-12 shrink-0 rounded-xl object-cover border border-gray-200"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-400">
                          <FaBoxOpen className="h-5 w-5" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{name}</p>
                        {ri.product_id != null && (
                          <p className="text-[10px] text-gray-400">Product ID: {ri.product_id}</p>
                        )}
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {qty != null ? `Qty: ${qty}` : ""}{unit}
                        </p>
                        {ri.remark && (
                          <p className="text-[10px] italic text-gray-400 mt-0.5 truncate">{ri.remark}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pickup Details */}
          {pickup && (
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4">
              <div className="flex items-center gap-1.5 mb-3">
                <FaTruck className="h-4 w-4 text-indigo-600" />
                <p className="text-sm font-bold text-indigo-900">Scheduled Pickup</p>
              </div>
              {(pickup.pickup_date || pickup.pickup_time) && (
                <div className="flex items-center gap-2 mb-2">
                  <FaCalendarAlt className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                  <p className="text-sm font-extrabold text-indigo-900">
                    {pickup.pickup_date}
                    {pickup.pickup_time ? ` \u00B7 ${pickup.pickup_time}` : ""}
                  </p>
                </div>
              )}
              {pickup.pickup_address && (
                <div className="flex items-start gap-2">
                  <FaMapMarkerAlt className="h-3.5 w-3.5 text-indigo-500 shrink-0 mt-0.5" />
                  <div>
                    {pickup.pickup_address.name && (
                      <p className="text-xs font-bold text-gray-800">{pickup.pickup_address.name}</p>
                    )}
                    <p className="text-[11px] text-gray-500 leading-relaxed">
                      {pickup.pickup_address.address_line || ""}
                      {pickup.pickup_address.city ? `, ${pickup.pickup_address.city}` : ""}
                      {pickup.pickup_address.state ? `, ${pickup.pickup_address.state}` : ""}
                      {pickup.pickup_address.pincode ? ` - ${pickup.pickup_address.pincode}` : ""}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Refund Information */}
          {refund && (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <FaMoneyBillWave className="h-4 w-4 text-emerald-600" />
                  <p className="text-sm font-bold text-emerald-900">Refund Information</p>
                </div>
                {refund.refund_status && (
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                    refund.refund_status === "PAID" || refund.refund_status === "COMPLETED"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-gray-100 text-gray-700"
                  }`}>
                    {refund.refund_status}
                  </span>
                )}
              </div>

              <div className="space-y-2 text-xs">
                {refundFields.map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 border-b border-emerald-100/60 py-1 last:border-0">
                    <span className="text-gray-500">{label}</span>
                    <span className="break-all text-right font-semibold text-gray-700">
                      {value == null || value === "" ? "—" : value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Images */}
          <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-3.5">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
              Images ({detail.images?.length ?? 0})
            </p>
            {detail.images?.length ? (
              <div className="flex flex-wrap gap-2">
                {detail.images.map((image, index) => (
                  <a key={image.url ?? index} href={image.url} target="_blank" rel="noreferrer">
                    <img src={image.url} alt={`Return attachment ${index + 1}`} className="h-16 w-16 rounded-lg border border-gray-200 object-cover" />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500">No images attached.</p>
            )}
          </div>

          {/* Notes */}
          <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Notes</p>
            <p className="text-xs italic text-gray-600">{detail.notes || "No notes provided."}</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

/* --- Return List Card --- */

const ReturnCard = ({ item, onClick }) => {
  const statusKey  = (item.status ?? "UNDER_REVIEW").toUpperCase();
  const cfg        = getStatusConfig(statusKey);
  const StatusIcon = STATUS_ICON[statusKey] ?? FaClock;
  const displayRef = item.return_ref ?? item.return_request_number ?? `#${item.return_id}`;

  const returnItems = item.return_items?.length
    ? item.return_items
    : Array.isArray(item.items) ? item.items : [];

  const headerProductImages = returnItems
    .map((ri) => ri.image ?? ri.product_image ?? null)
    .filter(Boolean)
    .slice(0, 3);
  if (headerProductImages.length === 0 && item.image) headerProductImages.push(item.image);

  const displayOrderNumber =
    item.order_number ?? item.order?.order_number ?? (item.order_id ? `#${item.order_id}` : "");

  return (
    <div
      onClick={onClick}
      className="group cursor-pointer overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm hover:border-emerald-200 hover:shadow-md transition-all duration-200"
    >
      <div className={`h-1 w-full ${cfg.bg}`} />

      <div className="flex flex-col gap-3 border-b border-gray-100 bg-gray-50/60 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
        <div className="flex items-start gap-3 min-w-0">
          {/* Product image stack */}
          {headerProductImages.length > 0 && (
            <div className="relative shrink-0 flex">
              {headerProductImages.map((imgSrc, idx) => (
                <img
                  key={idx}
                  src={buildAssetUrl(imgSrc)}
                  alt="product"
                  style={{ zIndex: headerProductImages.length - idx, marginLeft: idx === 0 ? 0 : -10 }}
                  className="h-14 w-14 rounded-2xl object-cover border-2 border-white shadow"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ))}
            </div>
          )}

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Return Ref</span>
              <span className="font-mono text-sm font-extrabold text-gray-900">{displayRef}</span>
              <span className="rounded-md bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                {item.return_type_label ?? item.return_type}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {displayOrderNumber && (
                <>For Order <span className="font-mono font-bold text-gray-800">{displayOrderNumber}</span></>
              )}
              {(item.requested_at || item.created_at) && (
                <> &middot; {formatDate(item.requested_at ?? item.created_at)}</>
              )}
            </p>
            {returnItems.length > 0 && (
              <p className="mt-1 text-[11px] text-gray-400 truncate max-w-xs">
                {returnItems.map((ri) => ri.product_name ?? ri.name).filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1 text-xs font-bold ${cfg.bg} ${cfg.text} ${cfg.border}`}>
            <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
            <StatusIcon className="h-3 w-3" />
            {item.status_label ?? cfg.label}
          </span>
          <FaChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-emerald-500 transition" />
        </div>
      </div>

      <div className="flex items-center justify-between px-5 py-3 text-xs text-gray-500">
        {item.refund_amount > 0 ? (
          <span>
            Refund: <span className="font-extrabold text-emerald-700">{fmtRs(item.refund_amount)}</span>
          </span>
        ) : <span />}
        <span className="text-[11px] text-emerald-600 font-semibold group-hover:underline">
          View Details {"\u2192"}
        </span>
      </div>
    </div>
  );
};

/* --- Main ReturnsSection --- */

const ReturnsSection = () => {
  const navigate = useNavigate();
  const { returns, returns_loading, returns_error, returns_filter, fetchReturns, setReturnsFilter } = useStore();
  const [selectedReturn, setSelectedReturn] = useState(null);

  // Load returns without a status filter so API status aliases can be grouped
  // consistently in the UI tabs.
  useEffect(() => {
    fetchReturns({ status: "ALL", limit: 100 });
  }, [fetchReturns]);

  // Poll every 10s while mounted
  useEffect(() => {
    const id = setInterval(() => { fetchReturns({ status: "ALL", limit: 100 }); }, 10_000);
    return () => clearInterval(id);
  }, [fetchReturns]);

  const visibleReturns = returns_filter === "ALL"
    ? returns
    : returns.filter((item) => item.status === returns_filter);

  return (
    <div>
      {/* Header */}
      <div className="mb-5 flex flex-col gap-3 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-extrabold text-gray-900">Returns &amp; Refunds</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Tap any return to see full details, timeline, pickup &amp; refund info
          </p>
        </div>
        <button
          onClick={() => fetchReturns({ status: "ALL", limit: 100 })}
          disabled={returns_loading}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 active:scale-95"
        >
          <FaSyncAlt className={`h-3.5 w-3.5 ${returns_loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex gap-2 overflow-x-auto pb-1 scrollbar-none">
        {FILTER_TABS.map((tab) => {
          const isActive = returns_filter === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setReturnsFilter(tab.id)}
              className={`shrink-0 rounded-xl px-4 py-2 text-xs font-bold transition-all duration-200 ${
                isActive
                  ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Loading skeleton */}
      {returns_loading && !returns.length && (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse rounded-2xl border border-gray-100 bg-white p-5">
              <div className="flex justify-between">
                <div className="flex gap-3">
                  <div className="h-14 w-14 rounded-2xl bg-gray-100" />
                  <div className="space-y-2 pt-1">
                    <div className="h-3 w-32 rounded bg-gray-100" />
                    <div className="h-3 w-48 rounded bg-gray-100" />
                  </div>
                </div>
                <div className="h-6 w-24 rounded-full bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {returns_error && !returns_loading && (
        <div className="mb-4 flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          <FaTimesCircle className="h-5 w-5 shrink-0 text-red-400" />
          <span>{returns_error}</span>
        </div>
      )}

      {/* Empty state */}
      {!returns_loading && !returns_error && visibleReturns.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 px-6 py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
            <FaUndo className="h-7 w-7 text-gray-300" />
          </div>
          <p className="mt-4 text-base font-bold text-gray-800">No return requests found</p>
          <p className="mt-1 text-sm text-gray-500 max-w-sm">
            {returns_filter !== "ALL"
              ? `No returns found under status "${returns_filter}".`
              : "You haven't requested any returns or refunds yet."}
          </p>
          <button
            onClick={() => navigate("/account/orders")}
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-emerald-700 transition active:scale-95"
          >
            Go to My Orders
          </button>
        </div>
      )}

      {/* Return cards list */}
      {!returns_loading && !returns_error && visibleReturns.length > 0 && (
        <div className="space-y-4">
          {visibleReturns.map((item) => (
            <ReturnCard
              key={item.return_ref ?? item.return_request_id ?? item.return_id ?? item.id}
              item={item}
              onClick={() => setSelectedReturn(item)}
            />
          ))}
        </div>
      )}

      {/* Detail Drawer */}
      {selectedReturn && (
        <ReturnDetailDrawer
          item={selectedReturn}
          onClose={() => setSelectedReturn(null)}
        />
      )}
    </div>
  );
};

export default ReturnsSection;
